import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import {
  careseekerLeadsService,
  CareseekerLead,
} from '../../services/careseekerLeadsService';

// FEAT-069 (MVP-18.11): curated careseeker leads — no open directory browse.

const CareseekerLeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<CareseekerLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await careseekerLeadsService.listLeads();
      setLeads(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'We had trouble loading your curated careseeker list.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4">Careseeker Leads</Typography>
        <Typography variant="body1" color="text.secondary">
          A curated list matched to your service areas. You cannot browse the full
          careseeker directory.
        </Typography>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress data-testid="careseeker-leads-loading" />
        </Box>
      )}

      {error && (
        <Alert severity="error" data-testid="careseeker-leads-error">
          {error}
        </Alert>
      )}

      {!loading && !error && leads.length === 0 && (
        <Alert severity="info" data-testid="careseeker-leads-empty">
          No curated leads right now. Check back after updating your service areas.
        </Alert>
      )}

      <Stack spacing={2} data-testid="careseeker-leads-list">
        {leads.map((lead) => (
          <Card key={lead.id}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Avatar src={lead.avatar ?? undefined}>
                  <PeopleOutlineIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">
                    {[lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'Careseeker'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {lead.matchReason}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {lead.careNeeds.map((need) => (
                      <Chip key={need} label={need} size="small" />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Container>
  );
};

export default CareseekerLeadsPage;
